import { useCallback, useState } from 'react';

/**
 * Colour theme — an option, not a takeover.
 *
 * Three states: follow the operating system, force light, force dark. Following
 * the OS is the default because that is a setting the user has already made once,
 * for every other application on the machine; a portal that ignores it and opens
 * white at 21:00 is the thing this exists to stop.
 */

export type ThemeChoice = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'kt.reporting.theme';
const ATTRIBUTE = 'data-kt-theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/*
 * The choice and the resolved theme are deliberately two different values, and
 * only the resolved one ever reaches the DOM.
 *
 * localStorage holds the choice, which may be "system"; <html data-kt-theme> only
 * ever holds "light" or "dark". That is what lets theme.css state the dark palette
 * exactly once, keyed off a single attribute value, instead of repeating all forty
 * tokens a second time inside a prefers-color-scheme query — two copies of a
 * palette drift the moment somebody adds a token to one of them and not the other.
 */

function prefersDark() {
  return window.matchMedia?.(DARK_QUERY).matches === true;
}

export function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  if (choice !== 'system') return choice;
  return prefersDark() ? 'dark' : 'light';
}

/*
 * Storage is allowed to fail rather than return null — Safari's private mode and a
 * blocked-storage policy both throw on access, not on read. A colour preference is
 * not worth a blank page, so both directions fall back to following the OS.
 */
export function readThemeChoice(): ThemeChoice {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'system' || stored === 'light' || stored === 'dark') return stored;
  } catch {
    // No storage available; the OS preference is a perfectly good answer.
  }
  return 'system';
}

function writeThemeChoice(choice: ThemeChoice) {
  try {
    localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // The theme still applies for this tab; it just will not survive a reload.
  }
}

function applyTheme(choice: ThemeChoice) {
  document.documentElement.setAttribute(ATTRIBUTE, resolveTheme(choice));
}

/**
 * Called from main.tsx before the first render, so the attribute is on <html>
 * before React paints anything. theme.css carries a matching prefers-color-scheme
 * fallback for the page background alone, which covers the one frame between the
 * stylesheet arriving and this module running.
 */
export function startTheme() {
  applyTheme(readThemeChoice());

  // An OS that flips to dark on a schedule should carry a tab that is already
  // open — but only for someone who asked to follow it. Reading the choice inside
  // the listener rather than closing over it keeps that true after a later change.
  window.matchMedia?.(DARK_QUERY).addEventListener('change', () => {
    if (readThemeChoice() === 'system') applyTheme('system');
  });
}

/**
 * The AppBar control's state. Seeded from storage rather than from a default, so
 * the buttons agree with the page on the very first render instead of correcting
 * themselves in an effect.
 */
export function useThemeChoice(): [ThemeChoice, (next: ThemeChoice) => void] {
  const [choice, setChoice] = useState<ThemeChoice>(readThemeChoice);

  const update = useCallback((next: ThemeChoice) => {
    writeThemeChoice(next);
    applyTheme(next);
    setChoice(next);
  }, []);

  return [choice, update];
}
